import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';

import * as DM from '@radix-ui/react-dropdown-menu';
import { type VariantProps, cva } from 'class-variance-authority';
import clsx from 'clsx';

import { ReactComponent as Triangle } from '@quick/assets/icons/triangle.svg';
import { Button } from '@quick/components/Button';
import type { ButtonVariantProps } from '@quick/components/Button/Button';
import { cn } from '@quick/utils';

export const tagStyles =
  'text-gray-700 dark:text-gray-300 group-data-[disabled]:text-gray-500 ml-auto pl-5 group-data-[highlighted]:text-white';

export function SplitButton({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        `*:border-background-group dark:*:border-background-group-dark flex
        *:rounded-none *:border-r-2 first:*:rounded-l last:*:rounded-r last:*:border-r-0`,
        className
      )}
    >
      {children}
    </div>
  );
}

export const childCStyles = cva(
  'group relative flex h-8 select-none items-center rounded px-1 pl-6 text-sm leading-none outline-none data-[disabled]:pointer-events-none cursor-pointer',
  {
    variants: {
      variant: {
        primary:
          'text-gray-800 data-[disabled]:text-gray-400 dark:data-[disabled]:text-gray-600 data-[highlighted]:text-gray-800 dark:data-[highlighted]:text-gray-800 dark:text-gray-200 data-[highlighted]:bg-brand',
        accept:
          'text-green-800 data-[disabled]:text-green-300 dark:data-[disabled]:text-green-900 data-[highlighted]:text-white dark:data-[highlighted]:text-white dark:text-green-600 data-[highlighted]:bg-green-700',
        destruct:
          'text-red-600 data-[disabled]:text-red-300 dark:data-[disabled]:text-red-900 data-[highlighted]:text-white dark:data-[highlighted]:text-white dark:text-red-600 data-[highlighted]:bg-red-600',
        warning:
          'text-orange-500 data-[disabled]:text-orange-300 dark:data-[disabled]:text-orange-900 data-[highlighted]:text-white dark:data-[highlighted]:text-white dark:text-orange-500 data-[highlighted]:bg-orange-500',
        black:
          'text-gray-800 data-[disabled]:text-gray-400 dark:data-[disabled]:text-gray-600 data-[highlighted]:text-white dark:data-[highlighted]:text-black dark:text-gray-200 data-[highlighted]:bg-black dark:data-[highlighted]:bg-white',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  }
);

interface ChildProps
  extends VariantProps<typeof childCStyles>,
    ComponentProps<typeof DM.Item> {
  children: React.ReactNode;
  className?: string;
}

interface MenuProps extends ButtonVariantProps, PropsWithChildren {
  className?: string;
  trigger?: ReactNode;
  container?: HTMLElement;
}

const contentClassName = `data-[side=top]:animate-slideDownAndFade shadow
            data-[side=right]:animate-slideLeftAndFade
            data-[side=bottom]:animate-slideUpAndFade
            data-[side=left]:animate-slideRightAndFade bg-background-group
            dark:bg-background-group-dark min-w-[220px] rounded-md p-1
            shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]
            will-change-[opacity,transform]`;
export function Menu({
  children,
  className,
  trigger,
  variant,
  kind,
  container,
}: MenuProps) {
  return (
    <DM.Root>
      <DM.Trigger asChild>
        <Button className="group flex gap-2" variant={variant} kind={kind}>
          {trigger}
          <span className="transform transition-transform group-data-[state=open]:rotate-180">
            <Triangle className="size-3" />
          </span>
        </Button>
      </DM.Trigger>
      <DM.Portal container={container}>
        <DM.Content
          className={clsx(contentClassName, className)}
          sideOffset={5}
        >
          {children}

          <DM.Arrow className="fill-background-group dark:fill-background-group-dark" />
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

export function Child({ children, className, variant, ...props }: ChildProps) {
  const cls = childCStyles({ variant, className });
  return (
    <DM.Item className={cls} {...props}>
      {children}
    </DM.Item>
  );
}

export function Sub({
  children,
  className,
  variant,
  trigger,
  menuClassName,
  ...props
}: ChildProps & { trigger: ReactNode; menuClassName?: string }) {
  const cls = childCStyles({
    variant,
    className: clsx('justify-between', className),
  });
  return (
    <DM.Sub {...props}>
      <DM.SubTrigger className={cls}>
        {trigger}
        <span className="rotate-90 transform">
          <Triangle className="size-3" />
        </span>
      </DM.SubTrigger>
      <DM.SubContent className={cn(contentClassName, menuClassName)}>
        {children}
      </DM.SubContent>
    </DM.Sub>
  );
}
